import { writeBatch } from 'firebase/firestore'
import { ParentContext } from 'global/cnr/contexts/ParentContext'
import { removeFromObjects } from 'global/common/sorting'
import { createRefPath, createRefPath_client, createRefPath_event } from 'global/firestoreData/appData/appRefPaths'
import { fsUpdate_subCollection } from 'global/firestoreData/updates/subUpdates/fsUpdate_subCollection'
import { useCallback, useContext, useState } from 'react'
import { GolfParentContext } from '../cnr/contexts/GolfParentContext'
import { GolfTournamentContext } from '../cnr/contexts/GolfTournamentContext'
import { golfTournamentHelpers } from '../cnr/reducers/reducerHelpers/golfTournamentHelpers'

export const _allowGolfUpdates = false

const colsAndDocs = {
  golfTournament: 'golfTournaments',
  details: 'details'
}

const _clientCollections = ['_golfCourses'];
const eventCollections = ['teams', 'golfers', 'golfTournaments'];

export const useGolf = () => {

  const parentContext = useContext(ParentContext)
  const { fns, states } = parentContext ?? {}
  const { app_state, paps_state } = states ?? {}
  const { mainDb } = app_state ?? {}
  const { pathViews } = paps_state ?? {}
  const { database_fns } = fns

  const golfParentContext = useContext(GolfParentContext)
  const { golfParent_cls } = golfParentContext ?? {}
  const { createGolfTournament } = golfParent_cls ?? {}

  const golfTournamentContext = useContext(GolfTournamentContext)
  const { golfTournament_state } = golfTournamentContext ? golfTournamentContext : {}
  const { golfTournament: gt, golfCourse, golfTeam1, golfTeam2 } = golfTournament_state ?? {}
  const { id: gtid } = gt ?? {}
  const { golfStyles } = golfTournament_state ?? {}

  const [updating, setUpdating] = useState()
  const [tournamentPreview, setTournamentPreview] = useState()

  const create_golfTournamentPreview = useCallback(async ({ tournamentDetails, teams_event }) => {
    setUpdating(true);
    try {
      const { success, result } = await createGolfTournament(pathViews, {}, tournamentDetails, teams_event, true)
      setTournamentPreview(result)
      setUpdating(false);
    } catch (error) {
      console.error("Error saving handicaps:", error);
      setUpdating(false);
    } finally {
      setUpdating(false);
    }
  }, [])

  const create_golfTournament = useCallback(async ({ tournamentDetails, teams_event }) => {
    setUpdating(true);
    try {
      const { success, result } = await createGolfTournament(pathViews, {}, tournamentDetails, teams_event, true)
      if (success) {
        const batch = writeBatch(mainDb);
        const _result = _.cloneDeep(result)
        const { details } = _result
        const _golfTournament = _.omit(_result, ['details']);
        const gtKey = database_fns.get_docKey()
        const _gtRef = createRefPath_event(pathViews, ['golfTournaments', gtKey])
        //refPath, data, merge, dispatch, batch, altDb
        database_fns.set_doc(_gtRef, _golfTournament, false, null, batch)
        const _subRef = createRefPath_event(pathViews, ['golfTournaments', gtKey, 'details', 'details'])
        database_fns.set_doc(_subRef, details, false, null, batch)
        // console.log('_gtRef', _gtRef, _golfTournament)
        // console.log('_subRef', _subRef, details)
        if (_allowGolfUpdates) {
          await batch.commit()
        }
      }
      setUpdating(false);
    } catch (error) {
      console.error("Error create_golfTournament:", error);
      setUpdating(false);
    } finally {
      setUpdating(false);
    }
  }, [])

  const update_golfTournament = useCallback(async ({ details, callback }) => {
    setUpdating(true);
    try {
      await golfTournamentHelpers.updateGolfTournament(database_fns, gtid, pathViews, details, callback)
      setUpdating(false);
    } catch (error) {
      console.error("Error saving handicaps:", error);
      setUpdating(false);
    } finally {
      setUpdating(false);
    }
  }, [])

  const get_golfTournamentData = useCallback(({ golfTournamentKey, callback }) => {

    // const m = (m) => { console.log('m', m) }
    // const d = (d) => { console.log('d', d) }

    if (golfTournamentKey) {
      pathViews.golfTournaments = golfTournamentKey
    }

    const gte = null // golfTournaments?.[action.golfTournamentKey]
    // const callbacks = {
    //   // main: m,
    //   details: d,
    //   matches: m,
    //   // info: handleSet_tournamentInfo,
    //   // all: handleSet_mainData,
    // }
    const cb = (data) => callback(data)
    golfTournamentHelpers.getGolfTournamentWithDetails(database_fns, pathViews.golfTournaments, pathViews, null, gte, cb)
  }, [])

  const get_golfCourseStats = useCallback(({ golfCourse }) => {
    return golfCourseStats({ golfCourse })
  }, [])

  const handleCreate_roundMatches = useCallback(async ({
    matches,
    roundKey,
    callback,
    testOnly
  }) => {

    setUpdating(true);

    try {

      const result = await golfTournamentHelpers.createRoundMatchesInDatabase(
        database_fns,
        gtid,
        pathViews,
        matches,
        roundKey,
        callback,
        testOnly
      );

      return result;

    } catch (error) {
      console.error(error);
    } finally {
      setUpdating(false);
    }

  }, []);

  const clearTournamentResults = useCallback(async ({
    golfTournament,
    callback
  }) => {

    setUpdating(true);

    try {

      const result = await golfTournamentHelpers.clearTournamentResultsInDatabase(
        database_fns,
        gtid,
        golfTournament,
        pathViews,
        callback
      );

      return result;

    } catch (error) {
      console.error(error);
    } finally {
      setUpdating(false);
    }
  }, []);

  const clearRoundResults = useCallback(async ({
    roundKey,
    matches_round,
    callback
  }) => {

    setUpdating(true);

    try {

      const result = await golfTournamentHelpers.clearRoundResultsInDatabase(
        database_fns,
        gtid,
        pathViews,
        roundKey,
        matches_round,
        callback
      );

      return result;

    } catch (error) {
      console.error(error);
      setUpdating(false);
    } finally {
      setUpdating(false);
    }

  }, []);

  const clearRoundMatches = useCallback(async ({
    roundKey,
    callback
  }) => {

    setUpdating(true);

    try {

      const result = await golfTournamentHelpers.clearRoundMatchesInDatabase(
        database_fns,
        gtid,
        pathViews,
        roundKey,
        callback
      );

      return result;

    } catch (error) {

      console.error(error);
      throw error;

    } finally {

      setUpdating(false);

    }

  }, []);

  const clearTournamentMatches = useCallback(async ({
    roundKeys,
    callback
  }) => {

    setUpdating(true);

    try {

      const result = await golfTournamentHelpers.clearTournamentMatchesInDatabase(
        database_fns,
        gtid,
        pathViews,
        roundKeys,
        callback
      );

      return result;

    } catch (error) {

      console.error(error);
      throw error;

    } finally {

      setUpdating(false);

    }

  }, []);

  const updateRoundLineup = useCallback(async ({
    teamKey,
    roundType,
    lineup,
    callback
  }) => {

    setUpdating(true);

    try {

      const roundLineup = _.cloneDeep(lineup)
      removeFromObjects(roundLineup, ['courseTeeboxes', 'checked', 'name', 'lastName', 'firstName', 'qualifier', 'teamNumber', 'courseHandicap', 'courseHandicaps', 'courseRoundHandicap'])
      const _lineup = _.orderBy(roundLineup, ['groupNumber', 'position'])

      const gtlu = { [`lineups.${teamKey}.${roundType}`]: _lineup };

      console.log('gtlu', gtlu)

      if (_allowGolfUpdates) {
        const result = await golfTournamentHelpers.updateGolfTournamentDetails(
          database_fns,
          gtid,
          pathViews,
          gtlu,
          callback
        );
        if (callback) {
          // callback(result);
        }
      }
    } catch (error) {
      console.error(error);
      setUpdating(false);
    } finally {
      setUpdating(false);
    }

  }, []);

  const handleUpdate_round = useCallback(async ({
    roundType,
    selectedCourse,
    callback
  }) => {

    setUpdating(true);

    try {

      const gtlx = {
        [`rounds.${roundType}.golfCourse`]: selectedCourse
      };

      return gtlx;

    } catch (error) {

      console.error(error);
      throw error;

    } finally {

      setUpdating(false);

    }

  }, []);

  const handleUpdate_golferRecords = useCallback(async ({
    golferRecords
  }) => {
    setUpdating(true);
    try {
      if (golferRecords) {
        const partners = {}
        _.forEach(golferRecords, (gr, grKey) => {
          partners[grKey] = gr?.partners
        })
        const ref = createRefPath_event(pathViews, ['golfer_partners', gtid])
        console.log('ref', ref, partners)
        if (_allowGolfUpdates) {
          database_fns.doc_set(ref, partners, true)
        }
      }
    } catch (error) {
      console.error(error);
    } finally {
      setUpdating(false);
    }

  }, []);

  const updateTournamentResults = useCallback(async ({
    tournamentStatus,
    roundsStatus,
    callback
  }) => {

    setUpdating(true);

    try {

      const {
        team1Points,
        team1Wins,
        team1Halved,
        team2Points,
        team2Wins,
        team2Halved
      } = tournamentStatus ?? {};

      const {
        golfTeam1Style,
        golfTeam2Style
      } = golfStyles ?? {};

      const _roundsStatus = {};

      _.forEach(roundsStatus, (rs, rsk) => {

        const {
          team1Points,
          team2Points
        } = rs ?? {};

        _roundsStatus[rsk] = {
          team1Points,
          team2Points
        };

      });

      const dataToUpate = {
        results: {
          team1Points,
          team1Wins,
          team1Halved,
          team2Points,
          team2Wins,
          team2Halved,
          golfCourseName: golfCourse?.name,
          teamName1: golfTeam1?.name,
          teamName2: golfTeam2?.name,
          golfTeam1Style,
          golfTeam2Style,
          roundsStatus: _roundsStatus
        }
      };

      console.log('dataToUpate', dataToUpate)

      const result = await golfTournamentHelpers.updateGolfTournamentResults(
        database_fns,
        gtid,
        pathViews,
        dataToUpate
      );

      if (callback) {
        callback(result);
      }

      return result;

    } catch (error) {

      console.error(error);
      throw error;

    } finally {

      setUpdating(false);

    }

  }, []);

  const handleUpdate_golfTournament = useCallback(async ({
    details,
    callback
  }) => {

    setUpdating(true);

    try {

      const result = await golfTournamentHelpers.updateGolfTournament(
        database_fns,
        gtid,
        pathViews,
        details,
        callback
      );

      return result;

    } catch (error) {

      console.error(error);
      throw error;

    } finally {

      setUpdating(false);

    }

  }, []);

  const handleAmmend_roundsDateAndTime = useCallback(async ({
    round,
    callback
  }) => {

    setUpdating(true);

    try {

      const {
        startDate,
        startTime,
        key
      } = round;

      const updates_dt = {};

      updates_dt[`rounds.${key}.startDate`] = startDate;
      updates_dt[`rounds.${key}.startTime`] = startTime;

      const result = await golfTournamentHelpers.updateGolfTournamentDetails(
        database_fns,
        gtid,
        pathViews,
        updates_dt,
        callback
      );

      return result;

    } catch (error) {

      console.error(error);
      throw error;

    } finally {

      setUpdating(false);

    }

  }, []);

  const handleAmmend_roundsList = useCallback(async ({
    roundsList,
    callback
  }) => {

    setUpdating(true);

    try {

      const updates = {};

      roundsList.forEach((round) => {

        const {
          roundNumber,
          key
        } = round;

        updates[`rounds.${key}.roundNumber`] = roundNumber;

      });

      const result = await golfTournamentHelpers.updateGolfTournamentDetails(
        database_fns,
        gtid,
        pathViews,
        updates,
        callback
      );

      return result;

    } catch (error) {

      console.error(error);
      throw error;

    } finally {

      setUpdating(false);

    }

  }, []);

  const updateTournamentOptions = useCallback(async ({ localData }) => {
    setUpdating(true)
    const _basePath_tournament = createRefPath_event(pathViews, [colsAndDocs.golfTournament, gtid])
    if (_allowGolfUpdates) {
      await database_fns.update_docc({
        refPath: _basePath_tournament,
        data: localData
      })
    }

    setUpdating(false)
  }, []);

  const updateRoundOptions = useCallback(async ({ localData, roundKey }) => {
    setUpdating(true)
    const _basePath_round = createRefPath_event(pathViews, [colsAndDocs.golfTournament, gtid, colsAndDocs.details, colsAndDocs.details])
    const dataToUpdate = {}
    _.forEach(localData, (ldp, ldKey) => {
      if (ldp !== null && ldp !== undefined) {
        dataToUpdate['rounds.' + roundKey + '.' + ldKey] = ldp
      }
    })
    console.log('_basePath_round', _basePath_round, dataToUpdate)
    if (_allowGolfUpdates) {
      await database_fns.update_docc({
        refPath: _basePath_round,
        data: dataToUpdate
      })
    }
    setUpdating(false)
  }, []);

  const handleUpdate_golfCourse = useCallback(async ({ currentGolfCourse }) => {
    const cb = () => {
      setUpdating(false)
    }
    setUpdating(true)
    fsUpdate_subCollection(
      database_fns,
      { clients: pathViews.clients },
      {},
      'golfCourses',
      currentGolfCourse,
      cb
    )
    setUpdating(false)
  }, []);

  const get_golfClientData = useCallback(async ({ callback }) => {
    const _baseRef = createRefPath_client(pathViews)
    _clientCollections.forEach((collectionKey, index) => {
      const _clientRef = createRefPath([collectionKey], _baseRef)
      const isGrouped = collectionKey.startsWith('_')
      database_fns.get_data({ refPath: _clientRef }).then(subData => {
        if (isGrouped) {
          const subDataKey = collectionKey.substring(1)
          const _subData = subData[subDataKey][subDataKey]
          callback(_subData, subDataKey)
        } else {
          callback(subData, collectionKey)
        }
      })
    })
  }, []);

  const get_golfEventData = useCallback(async ({ callback }) => {
    const _baseRef = createRefPath_event(pathViews)
    eventCollections.forEach((viKey, index) => {
      const _getRef = createRefPath([viKey], _baseRef)
      database_fns.get_data({ refPath: _getRef, }).then(rd => {
        let dk = viKey
        callback(rd, dk)
      })
    })
  }, []);

  return {
    clearRoundMatches,
    clearRoundResults,
    clearTournamentResults,
    clearTournamentMatches,
    create_golfTournament,
    create_golfTournamentPreview,
    get_golfClientData,
    get_golfCourseStats,
    get_golfEventData,
    get_golfTournamentData,
    handleAmmend_roundsDateAndTime,
    handleAmmend_roundsList,
    handleCreate_roundMatches,
    handleUpdate_golfCourse,
    handleUpdate_golferRecords,
    handleUpdate_golfTournament,
    handleUpdate_round,
    update_golfTournament,
    updateRoundLineup,
    updateRoundOptions,
    updateTournamentOptions,
    updateTournamentResults,
    tournamentPreview,
    updating
  }
}

function golfCourseStats({ golfCourse }) {
  if (!golfCourse?.holes) return;
  return _.mapValues(
    golfCourse.teeBoxes,
    (teeBox, teeBoxKey) => ({
      ...teeBox,
      teeBoxKey,
      totalYardage: Object.values(golfCourse.holes)
        .reduce((total, hole) => total + (hole[teeBoxKey] || 0), 0)
    })
  )
}

